import { HubConnection } from "@microsoft/signalr";

import { featureManager } from "@/config/features";
import { apiClient } from "@/core/api/ApiClient";
import {
  ChatAcknowledgedDto,
  ChatActiveAnyActivityAnyPerformedMinimalDto,
  ChatActivityPerformedDto,
  ChatAnyActivityReadAnyPerformedMinimalDto,
  ChatEventReceivedDto,
  ChatHistoryItemCreatedDto,
  ChatHistoryItemDeletedDto,
  ChatHistoryItemPinStatusChangedDto,
  ChatHistoryItemUpdatedDto,
  ChatHubClientMethodName,
  ChatHubServerMethodName,
  ChatMessageAcknowledgedDto,
  ChatMessageDeletedDto,
  ChatMessageSentDto,
  ChatMessageUpdatedDto,
  ChatMessagesReadDto,
  ChatParticipantConnectedDto,
  ChatParticipantDisconnectedDto,
  ChatParticipantJoinedDto,
  ChatParticipantLeftDto,
  ChatParticipantOnlineStatus,
  ChatParticipantRemovedDto,
  ChatParticipantStatusUpdatedDto,
  ChatParticipantsAddedDto,
  ChatResolvedDto,
  ChatSemiActiveAnyActivityAnyPerformedMinimalDto,
  ChatUpdatedDto,
  ConnectToChatDto,
  DisconnectFromChatDto,
  ReportMyChatParticipantStatusDto,
  SubscribeToChatActivityUpdatesDto,
  UnsubscribeFromChatActivityUpdatesDto,
} from "@/core/api/generated/v1";
import store from "@/store";
import * as chatActivitySlice from "@/store/communication/chatActivityOverviewSlice";
import * as chatHistorySlice from "@/store/communication/chatHistorySlice";
import * as chatMessagesSlice from "@/store/communication/chatMessagesSlice";
import * as chatParticipantsSlice from "@/store/communication/chatParticipantsSlice";
import * as chatsSlice from "@/store/communication/chatsSlice";
import {
  selectChatCurrentParticipant,
  selectChatParticipantOnlineStatus,
  selectChatsWithMeInParticipants,
  selectCurrentUserChatParticipants,
} from "@/store/communication/selectors";

import { FeatureName } from "../constants/featureName";
import { IdHelper } from "../helpers/id";
import { BaseHubService } from "./baseHubService";
import { HubSubscription } from "./hubSubscription";

export { ChatHubClientMethodName, ChatHubServerMethodName };

/** Client methods that can be called by the server. */
export const clientMethods = ChatHubClientMethodName;

/** Server methods that can be called by the client. */
export const serverMethods = ChatHubServerMethodName;

export class ChatHubService extends BaseHubService {
  constructor() {
    super({
      hubName: "ChatHub",
      showLogs: true,
      logIncomingMessages: true,
      logOutcomingMessages: true,
    });

    this.on("initialized", ({ connection }) => {
      this.registerMethodHandlers(connection);
    });

    this.on("connected", ({ connectionId }) => {
      apiClient.updateSignalConnectionIds({
        chatHubConnectionId: connectionId,
      });

      // this.echo("Hello");

      // I am online now
      if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
        // mark me as online locally
        const currentParticipants = selectCurrentUserChatParticipants(store.getState());
        for (const participant of currentParticipants) {
          store.dispatch(
            chatParticipantsSlice.updateParticipantStatusInAllChats({
              participantId: participant.id,
              status: {
                onlineStatus: ChatParticipantOnlineStatus.Online,
              },
            }),
          );
        }

        // report my online status and ask everyone for their statuses
        const chats = selectChatsWithMeInParticipants(store.getState());
        for (const chat of chats) {
          const currentParticipant = selectChatCurrentParticipant(store.getState(), chat.id);
          this.reportMyChatParticipantStatus({
            chatId: chat.id,
            participantId: currentParticipant!.id,
            onlineStatus: ChatParticipantOnlineStatus.Online,
            onlyToConnectionIds: undefined,
            isReportBack: true,
          });
        }
      }
    });

    this.on("disconnected", () => {
      // everyone is offline
      if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
        store.dispatch(
          chatParticipantsSlice.updateAllParticipantsStatusInAllChats({
            status: {
              onlineStatus: ChatParticipantOnlineStatus.Offline,
            },
          }),
        );
      }
    });

    this.on("reconnecting", () => {
      // everyone is offline
      if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
        store.dispatch(
          chatParticipantsSlice.updateAllParticipantsStatusInAllChats({
            status: {
              onlineStatus: ChatParticipantOnlineStatus.Offline,
            },
          }),
        );
      }
    });
  }

  public async connect(): Promise<void> {
    await super.connectToHub("/signalr/chathub");
  }

  public async disconnect(): Promise<void> {
    await super.disconnectFromHub();
  }

  private registerMethodHandlers(connection: HubConnection) {
    this.registerMethodHandler(clientMethods.TestNotify, this.onTestNotify.bind(this));
    this.registerMethodHandler(clientMethods.Echo, this.onEcho.bind(this));
    this.registerMethodHandler(
      clientMethods.ServerErrorOccurred,
      this.onServerErrorOccurred.bind(this),
    );

    // Participant
    this.registerMethodHandler(
      clientMethods.ParticipantConnected,
      this.onChatParticipantConnected.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ParticipantDisconnected,
      this.onChatParticipantDisconnected.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ParticipantStatusUpdated,
      this.onParticipantStatusUpdated.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ParticipantJoined,
      this.onParticipantJoined.bind(this),
    );
    this.registerMethodHandler(clientMethods.ParticipantLeft, this.onParticipantLeft.bind(this));

    this.registerMethodHandler(
      clientMethods.ParticipantsAdded,
      this.onParticipantsAdded.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ParticipantRemoved,
      this.onParticipantRemoved.bind(this),
    );

    // Activity
    this.registerMethodHandler(
      clientMethods.ChatActivityPerformed,
      this.onChatActivityPerformed.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatActiveAnyActivityAnyPerformedMinimal,
      this.onChatActiveAnyActivityAnyPerformedMinimal.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatSemiActiveAnyActivityAnyPerformedMinimal,
      this.onChatSemiActiveAnyActivityAnyPerformedMinimal.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatAnyActivityReadAnyPerformedMinimal,
      this.onChatAnyActivityReadAnyPerformedMinimal.bind(this),
    );

    // Chat
    this.registerMethodHandler(clientMethods.ChatUpdated, this.onChatUpdated.bind(this));
    this.registerMethodHandler(clientMethods.ChatResolved, this.onChatResolved.bind(this));
    this.registerMethodHandler(clientMethods.ChatReopened, this.onChatReopened.bind(this));
    this.registerMethodHandler(clientMethods.ChatAcknowledged, this.onChatAcknowledged.bind(this));

    // Message
    this.registerMethodHandler(clientMethods.MessageSent, this.onMessageSent.bind(this));
    this.registerMethodHandler(clientMethods.MessageUpdated, this.onMessageUpdated.bind(this));
    this.registerMethodHandler(clientMethods.MessageDeleted, this.onMessageDeleted.bind(this));
    this.registerMethodHandler(
      clientMethods.MessageAcknowledged,
      this.onMessageAcknowledged.bind(this),
    );
    this.registerMethodHandler(clientMethods.MessagesRead, this.onMessageRead.bind(this));

    // ChatEvent
    this.registerMethodHandler(
      clientMethods.ChatEventReceived,
      this.onChatEventReceived.bind(this),
    );

    // ChatHistory
    this.registerMethodHandler(
      clientMethods.ChatHistoryItemCreated,
      this.onChatHistoryItemCreated.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatHistoryItemUpdated,
      this.onChatHistoryItemUpdated.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatHistoryItemDeleted,
      this.onChatHistoryItemDeleted.bind(this),
    );
    this.registerMethodHandler(
      clientMethods.ChatHistoryItemPinStatusChanged,
      this.onChatHistoryItemPinStatusChanged.bind(this),
    );
  }

  //#region Method handlers

  private onTestNotify(data: any) {}

  private onEcho(data: any) {}

  private onServerErrorOccurred(data: any) {
    // always log error response from the BE
    console.error(this.logPrefix, "BE notified about server error:", data);
  }

  /** New participant manually informs everyone in the chat about connection. */
  private onChatParticipantConnected(data: ChatParticipantConnectedDto) {
    if (
      this.connection?.connectionId !== data.connectionId &&
      featureManager.isEnabled(FeatureName.ChatOnlineStatus)
    ) {
      store.dispatch(
        chatParticipantsSlice.updateParticipantStatus({
          chatId: data.chatId,
          participantId: data.participantId,
          status: {
            onlineStatus: ChatParticipantOnlineStatus.Online,
          },
        }),
      );

      // when someone new connected, report my status in the chat to the newly connected participant
      const currentParticipant = selectChatCurrentParticipant(store.getState(), data.chatId);
      if (currentParticipant && currentParticipant.id !== data.participantId) {
        const onlineStatus = selectChatParticipantOnlineStatus(
          store.getState(),
          data.chatId,
          currentParticipant.id,
        );
        onlineStatus &&
          this.reportMyChatParticipantStatus({
            chatId: data.chatId,
            participantId: currentParticipant.id,
            onlineStatus: onlineStatus,
            onlyToConnectionIds: data.connectionId ? [data.connectionId] : undefined,
          });
      }
    }
  }

  /** Participant manually informs everyone in the chat about disconnection. */
  private onChatParticipantDisconnected(data: ChatParticipantDisconnectedDto) {
    if (
      this.connection?.connectionId !== data.connectionId &&
      featureManager.isEnabled(FeatureName.ChatOnlineStatus)
    ) {
      store.dispatch(
        chatParticipantsSlice.updateParticipantStatus({
          chatId: data.chatId,
          participantId: data.participantId,
          status: {
            onlineStatus: ChatParticipantOnlineStatus.Offline,
          },
        }),
      );
    }
  }

  /** Server informs us about change in participant status. */
  private onParticipantStatusUpdated(data: ChatParticipantStatusUpdatedDto) {
    if (
      this.connection?.connectionId !== data.connectionId &&
      featureManager.isEnabled(FeatureName.ChatOnlineStatus)
    ) {
      store.dispatch(
        chatParticipantsSlice.updateParticipantStatus({
          chatId: data.chatId,
          participantId: data.participantId,
          status: {
            onlineStatus: data.onlineStatus,
          },
        }),
      );

      // report back if asked for
      if (data.isReportBack) {
        const currentParticipant = selectChatCurrentParticipant(store.getState(), data.chatId);

        if (currentParticipant) {
          this.reportMyChatParticipantStatus({
            chatId: data.chatId,
            participantId: currentParticipant.id,
            onlineStatus: ChatParticipantOnlineStatus.Online,
            onlyToConnectionIds: data.connectionId ? [data.connectionId] : undefined,
          });
        }
      }
    }
  }

  private onParticipantJoined(data: ChatParticipantJoinedDto) {
    store.dispatch(
      chatParticipantsSlice.chatParticipantJoined({
        chatId: data.chatId!,
        participant: data.participant!,
      }),
    );
  }

  private onParticipantLeft(data: ChatParticipantLeftDto) {
    store.dispatch(
      chatParticipantsSlice.chatParticipantLeft({
        chatId: data.chatId!,
        participant: data.participant!,
      }),
    );
  }

  private onParticipantsAdded(data: ChatParticipantsAddedDto) {
    store.dispatch(chatParticipantsSlice.chatParticipantsAdded(data));
  }

  private onParticipantRemoved(data: ChatParticipantRemovedDto) {
    store.dispatch(chatParticipantsSlice.chatParticipantRemoved(data));
  }

  private onChatActivityPerformed(data: ChatActivityPerformedDto) {
    const currentParticipant = selectChatCurrentParticipant(store.getState(), data.chatId);
    const isByMe = data.participantId && currentParticipant?.id === data.participantId;
    if (!isByMe) {
      store.dispatch(chatActivitySlice.chatActivityPerformed(data));
    }
  }

  private onChatActiveAnyActivityAnyPerformedMinimal(
    data: ChatActiveAnyActivityAnyPerformedMinimalDto,
  ) {}

  private onChatSemiActiveAnyActivityAnyPerformedMinimal(
    data: ChatSemiActiveAnyActivityAnyPerformedMinimalDto,
  ) {}

  private onChatAnyActivityReadAnyPerformedMinimal(
    data: ChatAnyActivityReadAnyPerformedMinimalDto,
  ) {}

  private onChatUpdated(data: ChatUpdatedDto) {
    store.dispatch(chatsSlice.chatUpdated(data));
  }

  private onChatResolved(data: ChatResolvedDto) {
    store.dispatch(chatsSlice.chatResolved(data));
  }

  private onChatReopened(data: ChatResolvedDto) {
    store.dispatch(chatsSlice.chatReopened(data));
  }

  private onChatAcknowledged(data: ChatAcknowledgedDto) {
    store.dispatch(chatsSlice.chatAcknowledged(data));
    store.dispatch(chatMessagesSlice.chatAcknowledged(data));
    store.dispatch(chatHistorySlice.chatAcknowledged(data));
  }

  private onMessageSent(data: ChatMessageSentDto) {
    store.dispatch(chatMessagesSlice.newChatMessageReceived(data));
    store.dispatch(chatHistorySlice.newChatMessageReceived(data));
  }

  private onMessageUpdated(data: ChatMessageUpdatedDto) {
    store.dispatch(chatMessagesSlice.chatMessageUpdated(data));
    store.dispatch(chatHistorySlice.chatMessageUpdated(data));
  }

  private onMessageDeleted(data: ChatMessageDeletedDto) {
    store.dispatch(chatMessagesSlice.chatMessageDeleted(data));
    store.dispatch(chatHistorySlice.chatMessageDeleted(data));
  }

  private onMessageAcknowledged(data: ChatMessageAcknowledgedDto) {
    store.dispatch(chatMessagesSlice.chatMessageAcknowledged(data));
    store.dispatch(chatHistorySlice.chatMessageAcknowledged(data));
  }

  private onMessageRead(data: ChatMessagesReadDto) {
    if (featureManager.isEnabled(FeatureName.ChatMessageReadStatus)) {
      store.dispatch(chatMessagesSlice.chatMessagesRead(data));
      store.dispatch(chatHistorySlice.chatMessagesRead(data));
    }
  }

  private onChatEventReceived(data: ChatEventReceivedDto) {
    store.dispatch(chatHistorySlice.newChatEventReceived(data));
  }

  private onChatHistoryItemCreated(data: ChatHistoryItemCreatedDto) {
    store.dispatch(chatHistorySlice.chatHistoryItemCreated(data));
  }

  private onChatHistoryItemUpdated(data: ChatHistoryItemUpdatedDto) {
    store.dispatch(chatHistorySlice.chatHistoryItemUpdated(data));
  }

  private onChatHistoryItemDeleted(data: ChatHistoryItemDeletedDto) {
    store.dispatch(chatHistorySlice.chatHistoryItemDeleted(data));
  }

  private onChatHistoryItemPinStatusChanged(data: ChatHistoryItemPinStatusChangedDto) {
    store.dispatch(chatHistorySlice.chatHistoryItemPinStatusChanged(data));
  }

  //#endregion

  //#region Server methods

  public echo(message: string) {
    this.send(serverMethods.Echo, message);
  }

  /** Join SignalR chat group */
  public async connectToChat(data: ConnectToChatDto) {
    if (!data.chatId) {
      throw new Error("Chat id is required.");
    }
    await this.ensureConnectedToHub();

    const subscription = new HubSubscription({
      identifier: `connectToChat:Chat:${data.chatId}`,
      subscribe: (async () => {
        await this.send(serverMethods.ConnectToChat, data);

        if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
          store.dispatch(
            chatParticipantsSlice.updateParticipantStatus({
              chatId: data.chatId,
              participantId: data.participantId,
              status: {
                onlineStatus: ChatParticipantOnlineStatus.Online,
              },
            }),
          );
        }
      }).bind(this),
      unsubscribe: (async () => {
        await this.ensureConnectedToHub();
        await this.send(serverMethods.DisconnectFromChat, data);
      }).bind(this),
    });
    await this.addSubscription(subscription);

    // cleanup function
    return subscription.unsubscribe;
  }

  /** Leave SignalR chat group */
  public async disconnectFromChat(data: DisconnectFromChatDto) {
    if (!data.chatId) {
      throw new Error("Chat id is required.");
    }

    await this.ensureConnectedToHub();
    await this.removeSubscription({
      identifier: `connectToChat:Chat:${data.chatId}`,
    });

    if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
      store.dispatch(
        chatParticipantsSlice.updateParticipantStatus({
          chatId: data.chatId,
          participantId: data.participantId,
          status: {
            onlineStatus: ChatParticipantOnlineStatus.Offline,
          },
        }),
      );
    }
  }

  private async reportMyChatParticipantStatus(data: ReportMyChatParticipantStatusDto) {
    if (!data.chatId) {
      return;
    }

    if (featureManager.isEnabled(FeatureName.ChatOnlineStatus)) {
      await this.ensureConnectedToHub();
      await this.send(serverMethods.ReportMyChatParticipantStatus, data);
    }
  }

  /** Explicit activity updates for specific chat sent by server only on demand. */
  public async subscribeToChatActivityUpdates(data: SubscribeToChatActivityUpdatesDto) {
    if (!data.chatId) {
      return;
    }
    if (!featureManager.isEnabled(FeatureName.ChatActivity)) {
      return;
    }
    await this.ensureConnectedToHub();

    const subscription = new HubSubscription({
      identifier: `subscribeToChatActivityUpdates:Chat:${data.chatId}`,
      subscribe: (() => {
        return this.send(serverMethods.SubscribeToChatActivityUpdates, data);
      }).bind(this),
      unsubscribe: (() => {
        return this.unsubscribeFromChatActivityUpdates(data);
      }).bind(this),
    });
    await this.addSubscription(subscription);

    // cleanup function
    return subscription.unsubscribe;
  }

  public async unsubscribeFromChatActivityUpdates(data: UnsubscribeFromChatActivityUpdatesDto) {
    if (!data.chatId) {
      return;
    }

    if (featureManager.isEnabled(FeatureName.ChatActivity)) {
      await this.ensureConnectedToHub();
      await this.send(serverMethods.UnsubscribeFromChatActivityUpdates, data);
    }
  }

  public async subscribeToChatActiveAnyActivityAnyMinimal(
    callback: (params: { chatId: string }) => void,
  ) {
    const isEnabled = featureManager.isEnabled(FeatureName.ChatActivity);
    if (!isEnabled) {
      return;
    }
    await this.ensureConnectedToHub();

    const methodName = clientMethods.ChatActiveAnyActivityAnyPerformedMinimal;
    const methodHandler = (data: ChatActiveAnyActivityAnyPerformedMinimalDto) => {
      callback({ chatId: data.chatId });
    };

    const subscription = new HubSubscription({
      identifier: `subscribeToChatActiveAnyActivityAnyMinimal:${IdHelper.newTempFeId()}`,
      subscribe: (() => {
        this.registerMethodHandler(methodName, methodHandler);
        return this.send(serverMethods.SubscribeToChatActiveAnyActivityAnyMinimal);
      }).bind(this),
      unsubscribe: (() => {
        this.unregisterMethodHandler(methodName, methodHandler);
        return this.unsubscribeFromChatActiveAnyActivityAnyMinimal();
      }).bind(this),
    });
    await this.addSubscription(subscription);

    return subscription;
  }

  public async unsubscribeFromChatActiveAnyActivityAnyMinimal() {
    const isEnabled = featureManager.isEnabled(FeatureName.ChatActivity);
    if (!isEnabled) {
      return;
    }

    await this.ensureConnectedToHub();
    await this.send(serverMethods.UnsubscribeFromChatActiveAnyActivityAnyMinimal);
  }

  public async subscribeToChatSemiActiveAnyActivityAnyMinimal(
    callback: (params: { chatId: string }) => void,
  ) {
    const isEnabled = featureManager.isEnabled(FeatureName.ChatActivity);
    if (!isEnabled) {
      return;
    }
    await this.ensureConnectedToHub();

    const methodName = clientMethods.ChatSemiActiveAnyActivityAnyPerformedMinimal;
    const methodHandler = (data: ChatSemiActiveAnyActivityAnyPerformedMinimalDto) => {
      callback({ chatId: data.chatId });
    };

    const subscription = new HubSubscription({
      identifier: `subscribeToChatSemiActiveAnyActivityAnyMinimal:${IdHelper.newTempFeId()}`,
      subscribe: (() => {
        this.registerMethodHandler(methodName, methodHandler);
        return this.send(serverMethods.SubscribeToChatSemiActiveAnyActivityAnyMinimal);
      }).bind(this),
      unsubscribe: (() => {
        this.unregisterMethodHandler(methodName, methodHandler);
        return this.unsubscribeFromChatSemiActiveAnyActivityAnyMinimal();
      }).bind(this),
    });
    await this.addSubscription(subscription);

    return subscription;
  }

  public async unsubscribeFromChatSemiActiveAnyActivityAnyMinimal() {
    const isEnabled = featureManager.isEnabled(FeatureName.ChatActivity);
    if (!isEnabled) {
      return;
    }

    await this.ensureConnectedToHub();
    await this.send(serverMethods.UnsubscribeFromChatSemiActiveAnyActivityAnyMinimal);
  }

  public async subscribeToChatAnyActivityReadAnyMinimal(
    callback: (params: { chatId: string }) => void,
  ) {
    const isEnabled =
      featureManager.isEnabled(FeatureName.ChatActivity) &&
      featureManager.isEnabled(FeatureName.ChatMessageReadStatus);
    if (!isEnabled) {
      return;
    }
    await this.ensureConnectedToHub();

    const methodName = clientMethods.ChatAnyActivityReadAnyPerformedMinimal;
    const methodHandler = (data: ChatAnyActivityReadAnyPerformedMinimalDto) => {
      callback({ chatId: data.chatId });
    };

    const subscription = new HubSubscription({
      identifier: `subscribeToChatAnyActivityReadAnyMinimal:${IdHelper.newTempFeId()}`,
      subscribe: (() => {
        this.registerMethodHandler(methodName, methodHandler);
        return this.send(serverMethods.SubscribeToChatAnyActivityReadAnyMinimal);
      }).bind(this),
      unsubscribe: (() => {
        this.unregisterMethodHandler(methodName, methodHandler);
        return this.unsubscribeFromChatAnyActivityReadAnyMinimal();
      }).bind(this),
    });
    await this.addSubscription(subscription);

    return subscription;
  }

  public async unsubscribeFromChatAnyActivityReadAnyMinimal() {
    const isEnabled =
      featureManager.isEnabled(FeatureName.ChatActivity) &&
      featureManager.isEnabled(FeatureName.ChatMessageReadStatus);
    if (!isEnabled) {
      return;
    }

    await this.ensureConnectedToHub();
    await this.send(serverMethods.UnsubscribeFromChatAnyActivityReadAnyMinimal);
  }

  //#endregion

  // /** Implicit activity updates sent by the server. */
  // public subscribeToAnyActiveChatActivityUpdates(
  //   callback: (params: { chatId: string; isUnreadMessageCountShouldUpdate: boolean }) => void,
  // ) {
  //   const listener = (data: ChatActiveAnyActivityPerformedDto) => {
  //     callback({
  //       chatId: data.chatId,
  //       isUnreadMessageCountShouldUpdate:
  //         data.category === ChatActivityCategory.Message &&
  //         (
  //           [ChatActivityType.MessageSent, ChatActivityType.MessageDeleted] as ChatActivityType[]
  //         ).includes(data.type),
  //     });
  //   };

  //   this._localEventEmitter.on(LocalEventName.ChatActiveAnyActivity, listener);
  //   return {
  //     unsubscribe: () => {
  //       this._localEventEmitter.off(LocalEventName.ChatActiveAnyActivity, listener);
  //     },
  //   };
  // }

  // /** Implicit activity updates sent by the server. */
  // public subscribeToAnySemiActiveChatActivityUpdates(
  //   callback: (params: { chatId: string; isUnreadMessageCountShouldUpdate: boolean }) => void,
  // ) {
  //   const listener = (data: ChatSemiActiveAnyActivityPerformedDto) => {
  //     callback({
  //       chatId: data.chatId,
  //       isUnreadMessageCountShouldUpdate:
  //         data.category === ChatActivityCategory.Message &&
  //         (
  //           [ChatActivityType.MessageSent, ChatActivityType.MessageDeleted] as ChatActivityType[]
  //         ).includes(data.type),
  //     });
  //   };

  //   this._localEventEmitter.on(LocalEventName.ChatSemiActiveAnyActivity, listener);
  //   return {
  //     unsubscribe: () => {
  //       this._localEventEmitter.off(LocalEventName.ChatSemiActiveAnyActivity, listener);
  //     },
  //   };
  // }
}

export const chatHubService = new ChatHubService();
